import React, { useState, useCallback } from "react";
import styled from "styled-components";
import {
  getYouTubeThumbnailUrl,
  getYouTubeEmbedUrl,
} from "helpers/youtube";

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #000;
  overflow: hidden;
  cursor: pointer;
`;

const Thumbnail = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PlayButton = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  ${Wrapper}:hover & svg {
    opacity: 1;
  }

  svg {
    width: 68px;
    height: 48px;
    opacity: 0.85;
    transition: opacity 0.2s;
    filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.4));
  }
`;

const Iframe = styled.iframe`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: none;
`;

interface YouTubeEmbedProps {
  videoId: string;
  title?: string;
}

export function YouTubeEmbed({ videoId, title }: YouTubeEmbedProps) {
  const [activated, setActivated] = useState(false);

  const handleActivate = useCallback(() => {
    setActivated(true);
  }, []);

  if (activated) {
    return (
      <Wrapper>
        <Iframe
          src={`${getYouTubeEmbedUrl(videoId)}?autoplay=1&rel=0`}
          title={title || "YouTube video"}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper
      onClick={handleActivate}
      role="button"
      aria-label={title ? `Play ${title}` : "Play video"}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleActivate();
        }
      }}
    >
      <Thumbnail
        src={getYouTubeThumbnailUrl(videoId)}
        alt={title || "Video thumbnail"}
        loading="lazy"
      />
      <PlayButton>
        <svg viewBox="0 0 68 48" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M66.52 7.74c-.78-2.93-2.49-5.41-5.42-6.19C55.79.13 34 0 34 0S12.21.13 6.9 1.55C3.97 2.33 2.27 4.81 1.48 7.74.06 13.05 0 24 0 24s.06 10.95 1.48 16.26c.78 2.93 2.49 5.41 5.42 6.19C12.21 47.87 34 48 34 48s21.79-.13 27.1-1.55c2.93-.78 4.64-3.26 5.42-6.19C67.94 34.95 68 24 68 24s-.06-10.95-1.48-16.26z"
            fill="#f00"
          />
          <path d="M45 24 27 14v20" fill="#fff" />
        </svg>
      </PlayButton>
    </Wrapper>
  );
}
